import { Modal } from 'antd';
import { Row, Box, InputSearch } from 'basic-ui-lib';
import { Button } from 'basic-ui-lib/components/Button';
import { useState, forwardRef, useImperativeHandle, useRef, useMemo, useEffect } from 'react';
import { IMediaItem as ILibraryItem, IMediaItem, MediaType } from 'modules/media/interfaces/api';
import { ContentHeader, MediaTabContent } from './components';
import useGetManageLibrary from 'modules/media/services/hooks/useGetManageLibrary';
import useGetLibraryLinks from 'modules/media/services/hooks/useGetLibraryLinks';
import { useGetMedia } from 'modules/channel/services/hooks/useGetMedia';

// export type { SelectedContent } from 'modules/dashboard/interfaces/api';

type Props = {

};

type Ref = {  
  show: (currentContent?: IMediaItem[], onConfirm?: (content: IMediaItem[]) => void) => void;
  hide: () => void;
};

export type ContentModalRef = Ref;

export const useContentModalRef = () => useRef<Ref>(null);

const ContentModal = forwardRef<Ref, Props>(({}, ref) => {
  const [libraryData, fetchLibrary] = useGetManageLibrary();
  const [linksData, fetchLinks] = useGetLibraryLinks(true);
  const { items, pathStack, openFolder, onBack } = useGetMedia(libraryData, fetchLibrary);

  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('media');
  const [selection, setSelection] = useState<IMediaItem[]>([]);
  
  const onConfirmRef = useRef<(content: IMediaItem[]) => void>(() => undefined);

  const currentFolder = pathStack[pathStack.length - 1] ?? { label: '', path: '' };
  
  useEffect(() => {
    if (!open) return;

    if (!libraryData) {
      fetchLibrary(' ');
    }

    if (!linksData) {
      fetchLinks({ page: 1, size: 10 });
    }
  }, [open]);

  const onShow: Ref['show'] = (currentContent, onConfirm) => {
    if (onConfirm) {
      onConfirmRef.current = onConfirm;
    }
    setSelection(currentContent ?? []);
    setSearchQuery('');
    setOpen(true);
  };

  const onHide: Ref['hide'] = () => {
    setOpen(false);
  };

  useImperativeHandle(ref, () => ({
    show: onShow,
    hide: onHide,
  }));

  const handleConfirm = () => {
    if (selection) {
      onConfirmRef.current(selection);
    }
    onHide();
  };

  const handleClose = () => {
    onHide();
  };

  const handleMediaItemClick = (item: ILibraryItem) => {
    if (item.type === MediaType.FOLDER) {
      openFolder(item);
    } else {
      const currentSelection = selection;
      const isAlreadySelected = currentSelection.some(
        (selected) => selected.id === item.id);

      let newSelection: IMediaItem[];
      if (isAlreadySelected) {
        newSelection = currentSelection.filter(
          (selected) => !(selected.id === item.id));
      }
      else {
        newSelection = [...currentSelection, item];
      }
      
      setSelection(newSelection);
    }
  };

  const handleBackClick = () => {
    onBack();
  };

  // const handleLinkRowClick = (record: ILink) => {
  //   const currentSelection = selection || [];
  //   const isAlreadySelected = currentSelection.some(
  //     (selected) => selected.type === 'link' && selected.item.urlLink === record.urlLink);
  //   let newSelection: SelectedContent[];
  //   if (isAlreadySelected) {
  //     newSelection = currentSelection.filter(
  //       (selected) => !(selected.type === 'link' && selected.item.urlLink === record.urlLink));
  //   }
  //   else {
  //     newSelection = [...currentSelection, { type: 'link', item: record }];
  //   }
  //   setSelection(newSelection);
  // };

  // const handleTabChange = (tab: ContentType) => {
  //   setActiveTab(tab);
  //   setSearchQuery('');
  // };

  const selectedMediaIds = useMemo(
    () => selection.map(sel => sel.id)
  ,[selection]);
  
  // const selectedLinkId = useMemo(
  //   () => selection.filter(sel => sel.type === 'link')?.map(sel => sel.item.urlLink)
  // , [selection]);

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      closable={false}
      width={1048}
      centered
      destroyOnClose
      styles={{
        content: {
          padding: 0,
          borderRadius: 8,
        },
      }}
    >
      <Box display="flex" flexDirection="column" maxHeight="90vh">
        <ContentHeader onClose={handleClose} />

        {/* <ContentTabs
          activeTab={activeTab}
          mediaCount={mediaCount}
          linksCount={linksCount}
          onTabChange={handleTabChange}
        /> */}

        <Box padding="16px 24px">
          <InputSearch
            placeholder="Search folder/image/video"
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </Box>

        <Box height={400} overflow="auto" padding="0 24px">
          {/* {activeTab === 'media' ? (

          ) : (
            <LinkTabContent
              links={links}
              selectedLinkId={selectedLinkId}
              onLinkRowClick={handleLinkRowClick}
            />
          )} */}
            <MediaTabContent
              items={items}
              currentPath={currentFolder.path}
              folderName={currentFolder.label}
              selectedMediaIds={selectedMediaIds}
              onMediaItemClick={handleMediaItemClick}
              onBackClick={handleBackClick}
            />
        </Box>

        <Row justifyContent="center" padding="16px 24px 24px">
          <Button 
            type_second 
            lg 
            onClick={handleConfirm}
            disabled={selection.length === 0}
            style={{ minWidth: 240 }}
          >
            OK
          </Button>
        </Row>
      </Box>
    </Modal>
  );
});

ContentModal.displayName = 'ContentModal';

export default ContentModal;
